import { useCallback, useEffect, useRef, useState } from 'react'

export type VideoQuality = 'low' | 'medium' | 'high' | 'hd' | '4k'

export interface LocalMediaState {
  stream: MediaStream | null
  audioEnabled: boolean
  videoEnabled: boolean
  screenSharing: boolean
  quality: VideoQuality
  devices: {
    audioInput: MediaDeviceInfo[]
    videoInput: MediaDeviceInfo[]
    audioOutput: MediaDeviceInfo[]
  }
  selectedAudioIn: string | null
  selectedVideoIn: string | null
  selectedAudioOut: string | null
  error: string | null
}

const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
}

const VIDEO_QUALITY: Record<VideoQuality, MediaTrackConstraints> = {
  low:   { width: { ideal: 640  }, height: { ideal: 360  }, frameRate: { ideal: 15 } },
  medium:{ width: { ideal: 1280 }, height: { ideal: 720  }, frameRate: { ideal: 30 } },
  high:  { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
  hd:    { width: { ideal: 2560 }, height: { ideal: 1440 }, frameRate: { ideal: 30 } },
  '4k':  { width: { ideal: 3840 }, height: { ideal: 2160 }, frameRate: { ideal: 30 } },
}

// Persist user device preferences across sessions (per browser).
const PREF_KEYS = {
  audioIn: 'fvc.pref.audioIn',
  videoIn: 'fvc.pref.videoIn',
  audioOut: 'fvc.pref.audioOut',
  quality: 'fvc.pref.quality',
} as const

function readPref(key: string): string | null {
  try { return window.localStorage.getItem(key) } catch { return null }
}
function writePref(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch { /* ignore */ }
}

const VALID_QUALITIES: VideoQuality[] = ['low', 'medium', 'high', 'hd', '4k']
function readQualityPref(): VideoQuality {
  const saved = readPref(PREF_KEYS.quality)
  return (saved && (VALID_QUALITIES as string[]).includes(saved) ? saved : 'high') as VideoQuality
}

export function useLocalMedia() {
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [audioEnabled, setAudioEnabled] = useState(true)
  const [videoEnabled, setVideoEnabled] = useState(true)
  const [screenSharing, setScreenSharing] = useState(false)
  const [quality, setQuality] = useState<VideoQuality>(() => readQualityPref())
  const [devices, setDevices] = useState<LocalMediaState['devices']>({
    audioInput: [],
    videoInput: [],
    audioOutput: [],
  })
  const [selectedAudioIn, setSelectedAudioIn] = useState<string | null>(() => readPref(PREF_KEYS.audioIn))
  const [selectedVideoIn, setSelectedVideoIn] = useState<string | null>(() => readPref(PREF_KEYS.videoIn))
  const [selectedAudioOut, setSelectedAudioOut] = useState<string | null>(() => readPref(PREF_KEYS.audioOut))
  const [error, setError] = useState<string | null>(null)
  const qualityRef = useRef<VideoQuality>(quality)
  useEffect(() => { qualityRef.current = quality }, [quality])

  const cameraStreamRef = useRef<MediaStream | null>(null)
  const screenStreamRef = useRef<MediaStream | null>(null)

  const loadDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices()
      setDevices({
        audioInput: all.filter((d) => d.kind === 'audioinput'),
        videoInput: all.filter((d) => d.kind === 'videoinput'),
        audioOutput: all.filter((d) => d.kind === 'audiooutput'),
      })
      return all
    } catch {
      return [] as MediaDeviceInfo[]
    }
  }, [])

  const acquire = useCallback(
    async (audioDeviceId?: string, videoDeviceId?: string, q?: VideoQuality) => {
      setError(null)
      const videoBase = VIDEO_QUALITY[q ?? qualityRef.current]
      const constraints: MediaStreamConstraints = {
        audio: audioDeviceId
          ? { ...AUDIO_CONSTRAINTS, deviceId: { exact: audioDeviceId } }
          : AUDIO_CONSTRAINTS,
        video: videoDeviceId
          ? { ...videoBase, deviceId: { exact: videoDeviceId } }
          : videoBase,
      }
      try {
        const next = await navigator.mediaDevices.getUserMedia(constraints)
        // Stop previous camera tracks if any.
        cameraStreamRef.current?.getTracks().forEach((t) => t.stop())
        cameraStreamRef.current = next
        // Apply current mute state.
        for (const t of next.getAudioTracks()) t.enabled = audioEnabled
        for (const t of next.getVideoTracks()) t.enabled = videoEnabled
        setStream(next)
        await loadDevices()
        return next
      } catch (err) {
        setError((err as Error).message || 'Zugriff verweigert')
        return null
      }
    },
    [audioEnabled, videoEnabled, loadDevices],
  )

  // Initial acquisition. Try saved device preferences first; if that fails
  // (device disappeared since last session), retry with browser defaults.
  useEffect(() => {
    void (async () => {
      const savedAudio = readPref(PREF_KEYS.audioIn)
      const savedVideo = readPref(PREF_KEYS.videoIn)
      let acquired: MediaStream | null = null
      if (savedAudio || savedVideo) {
        acquired = await acquire(savedAudio ?? undefined, savedVideo ?? undefined)
      }
      if (!acquired) {
        // Fallback to defaults — also clears stale prefs in state so the
        // picker reflects the actually-used device.
        if (savedAudio || savedVideo) {
          setSelectedAudioIn(null)
          setSelectedVideoIn(null)
        }
        await acquire()
      }
    })()
    return () => {
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop())
      screenStreamRef.current?.getTracks().forEach((t) => t.stop())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handler = () => void loadDevices()
    navigator.mediaDevices.addEventListener('devicechange', handler)
    return () => navigator.mediaDevices.removeEventListener('devicechange', handler)
  }, [loadDevices])

  const toggleAudio = useCallback(() => {
    setAudioEnabled((prev) => {
      const next = !prev
      cameraStreamRef.current?.getAudioTracks().forEach((t) => (t.enabled = next))
      return next
    })
  }, [])

  const toggleVideo = useCallback(() => {
    setVideoEnabled((prev) => {
      const next = !prev
      cameraStreamRef.current?.getVideoTracks().forEach((t) => (t.enabled = next))
      return next
    })
  }, [])

  const switchAudio = useCallback(
    async (deviceId: string) => {
      setSelectedAudioIn(deviceId)
      writePref(PREF_KEYS.audioIn, deviceId)
      await acquire(deviceId, selectedVideoIn ?? undefined)
    },
    [acquire, selectedVideoIn],
  )

  const switchVideo = useCallback(
    async (deviceId: string) => {
      setSelectedVideoIn(deviceId)
      writePref(PREF_KEYS.videoIn, deviceId)
      await acquire(selectedAudioIn ?? undefined, deviceId)
    },
    [acquire, selectedAudioIn],
  )

  const switchQuality = useCallback(
    async (q: VideoQuality) => {
      setQuality(q)
      qualityRef.current = q
      writePref(PREF_KEYS.quality, q)
      await acquire(selectedAudioIn ?? undefined, selectedVideoIn ?? undefined, q)
    },
    [acquire, selectedAudioIn, selectedVideoIn],
  )

  const switchOutput = useCallback(async (deviceId: string) => {
    setSelectedAudioOut(deviceId)
    writePref(PREF_KEYS.audioOut, deviceId)
    // Applying output devices to <video> elements is done in VideoTile via setSinkId.
  }, [])

  const stopAllTracks = useCallback(() => {
    cameraStreamRef.current?.getTracks().forEach((t) => t.stop())
    cameraStreamRef.current = null
    screenStreamRef.current?.getTracks().forEach((t) => t.stop())
    screenStreamRef.current = null
    setStream(null)
    setScreenSharing(false)
  }, [])

  const startScreenShare = useCallback(async () => {
    const md = navigator.mediaDevices as MediaDevices & {
      getDisplayMedia?: (c?: MediaStreamConstraints) => Promise<MediaStream>
    }
    if (typeof md.getDisplayMedia !== 'function') {
      setError('Bildschirmfreigabe wird auf diesem Gerät nicht unterstützt')
      return
    }
    try {
      const display = await md.getDisplayMedia({ video: true, audio: false })
      const cam = cameraStreamRef.current
      if (!cam) return
      const screenTrack = display.getVideoTracks()[0]
      if (!screenTrack) return
      // Replace outgoing video track by swapping tracks in the combined stream.
      const existingVideo = cam.getVideoTracks()[0]
      if (existingVideo) {
        cam.removeTrack(existingVideo)
        existingVideo.stop()
      }
      cam.addTrack(screenTrack)
      screenStreamRef.current = display
      setScreenSharing(true)
      // When the user ends share via browser UI, restore camera.
      screenTrack.onended = () => void stopScreenShare()
      setStream(new MediaStream(cam.getTracks()))
    } catch (err) {
      setError((err as Error).message || 'Bildschirmfreigabe fehlgeschlagen')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopScreenShare = useCallback(async () => {
    const display = screenStreamRef.current
    if (display) {
      display.getTracks().forEach((t) => t.stop())
      screenStreamRef.current = null
    }
    setScreenSharing(false)
    // Re-acquire camera video.
    await acquire(selectedAudioIn ?? undefined, selectedVideoIn ?? undefined)
  }, [acquire, selectedAudioIn, selectedVideoIn])

  const screenShareSupported = typeof navigator !== 'undefined'
    && typeof navigator.mediaDevices?.getDisplayMedia === 'function'

  return {
    stream,
    audioEnabled,
    videoEnabled,
    screenSharing,
    screenShareSupported,
    quality,
    devices,
    selectedAudioIn,
    selectedVideoIn,
    selectedAudioOut,
    error,
    toggleAudio,
    toggleVideo,
    switchAudio,
    switchVideo,
    switchOutput,
    switchQuality,
    startScreenShare,
    stopScreenShare,
    stopAllTracks,
  }
}
